import React from 'react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import type { ContentRestrictionsSectionProps } from './index';
import OutgoingImagePolicySection from './OutgoingImagePolicySection';

export default function ContentRestrictionsSection({
  formData,
  onFormDataChange,
  workflowWidgets,
  language
}: ContentRestrictionsSectionProps) {
  const t = useLocalization(language);

  // Check which widgets are enabled to show relevant sections
  const hasInputImagesWidget = workflowWidgets.some(
    w => w.widget_type?.name === 'Input Images' && w.is_enabled
  );
  const hasInputMediaWidget = workflowWidgets.some(
    w => w.widget_type?.name === 'Input Media' && w.is_enabled
  );

  const config = formData.input_content_config || { images: {}, video: {}, audio: {} };

  const updateConfig = (type: 'images' | 'video' | 'audio', field: string, value: number | null) => {
    const newConfig = {
      ...config,
      [type]: {
        ...config[type],
        [field]: value
      }
    };
    onFormDataChange('input_content_config', newConfig);
  };

  // Handler for maxCount fields that allows 0 and empty values
  const handleMaxCountChange = (type: 'images' | 'video' | 'audio', e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    // Allow empty string to clear the field (will use default)
    if (rawValue === '') {
      updateConfig(type, 'maxCount', null);
      return;
    }
    const numValue = parseInt(rawValue, 10);
    // Allow 0 and positive numbers
    if (!isNaN(numValue) && numValue >= 0) {
      updateConfig(type, 'maxCount', numValue);
    }
  };

  const noWidgetsEnabled = !hasInputImagesWidget && !hasInputMediaWidget;

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Icon name={ICONS.SHIELD} size={20} className="text-orange-400" />
          {t.admin?.contentRestrictions || 'Content Restrictions'}
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          {t.admin?.contentRestrictionsDesc || 'Configure size and count limits for uploaded content.'}
        </p>
      </section>

      <OutgoingImagePolicySection
        policy={formData.settings?.outgoing_image_policy}
        language={language}
        onChange={policy => onFormDataChange('settings', { ...formData.settings, outgoing_image_policy: policy })}
      />

      {/* Prompt Character Limit */}
      <section className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-xl p-4">
        <h4 className="text-sm font-bold text-purple-300 mb-3 flex items-center gap-2">
          <Icon name={ICONS.PROMPTS} size={18} className="text-purple-400" />
          {t.admin?.promptCharLimit || 'Prompt Character Limit'}
        </h4>
        <div className="max-w-xs">
          <input
            type="number"
            value={formData.prompt_char_limit}
            onChange={(e) => onFormDataChange('prompt_char_limit', parseInt(e.target.value) || 5000)}
            min={100}
            max={50000}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
          />
          <p className="mt-2 text-xs text-gray-500">
            {t.admin?.promptCharLimitDesc || 'Default: 5000 (min: 100, max: 50000)'}
          </p>
        </div>
      </section>

      {/* No Widgets Warning */}
      {noWidgetsEnabled && (
        <section className="bg-yellow-900/20 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Icon name={ICONS.INFO} size={20} className="text-yellow-400 mt-0.5" />
            <div>
              <p className="text-yellow-300 font-medium">
                {t.admin?.noInputWidgetsEnabled || 'No input widgets enabled'}
              </p>
              <p className="text-sm text-yellow-400/80 mt-1">
                {t.admin?.noInputWidgetsEnabledDesc || 'Enable "Input Images" or "Input Media" widget in the Widgets section to configure content restrictions.'}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Image Size Constraints - For Input Images Widget */}
      {hasInputImagesWidget && (
        <section className="bg-gradient-to-br from-blue-900/20 to-purple-900/20 border border-blue-500/30 rounded-xl p-4">
          <h4 className="text-sm font-bold text-blue-300 mb-3 flex items-center gap-2">
            <Icon name={ICONS.IMAGE} size={18} className="text-blue-400" />
            {t.workflow?.imageSizeConstraints || 'Image Size Constraints'}
            <span className="text-xs font-normal text-gray-400 ml-2">
              ({t.admin?.forInputImages || 'for Input Images widget'})
            </span>
          </h4>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5">
                {t.workflow?.minWidth || 'Min Width'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={formData.min_image_width || ''}
                  onChange={(e) => onFormDataChange('min_image_width', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="px"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">px</span>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1.5">
                {t.workflow?.minHeight || 'Min Height'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={formData.min_image_height || ''}
                  onChange={(e) => onFormDataChange('min_image_height', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="px"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">px</span>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1.5">
                {t.workflow?.maxWidth || 'Max Width'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={formData.max_image_width || ''}
                  onChange={(e) => onFormDataChange('max_image_width', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="px"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">px</span>
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1.5">
                {t.workflow?.maxHeight || 'Max Height'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={formData.max_image_height || ''}
                  onChange={(e) => onFormDataChange('max_image_height', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="px"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">px</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-blue-400 mt-3">
            {t.workflow?.imageSizeConstraintsDesc || 'Set minimum and maximum image dimensions. Leave empty for no restrictions.'}
          </p>
        </section>
      )}

      {/* Media Content Config - For Input Media Widget */}
      {hasInputMediaWidget && (
        <section className="bg-gradient-to-br from-green-900/20 to-cyan-900/20 border border-green-500/30 rounded-xl p-4 space-y-4">
          <h4 className="text-sm font-bold text-green-300 mb-3 flex items-center gap-2">
            <Icon name={ICONS.UPLOAD} size={18} className="text-green-400" />
            {t.workflow?.mediaContentSettings || 'Media Content Settings'}
            <span className="text-xs font-normal text-gray-400 ml-2">
              ({t.admin?.forInputMedia || 'for Input Media widget'})
            </span>
          </h4>

          {/* Images Section */}
          <div className="border border-blue-500/30 rounded-lg p-4">
            <h5 className="text-blue-300 font-medium mb-3 flex items-center gap-2">
              <Icon name={ICONS.IMAGE} size={16} className="text-blue-400" />
              {t.workflow?.imagesSettings || 'Images'}
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxCount || 'Max Count'}
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={config.images?.maxCount ?? ''}
                  onChange={(e) => handleMaxCountChange('images', e)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-blue-500"
                  placeholder="12"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxFileSize || 'Max Size (MB)'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.images?.maxFileSize ?? ''}
                  onChange={(e) => updateConfig('images', 'maxFileSize', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-blue-500"
                  placeholder="20"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.minWidth || 'Min Width'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.images?.minWidth ?? ''}
                  onChange={(e) => updateConfig('images', 'minWidth', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-blue-500"
                  placeholder="px"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxWidth || 'Max Width'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.images?.maxWidth ?? ''}
                  onChange={(e) => updateConfig('images', 'maxWidth', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-blue-500"
                  placeholder="px"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.minHeight || 'Min Height'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.images?.minHeight ?? ''}
                  onChange={(e) => updateConfig('images', 'minHeight', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-blue-500"
                  placeholder="px"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxHeight || 'Max Height'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.images?.maxHeight ?? ''}
                  onChange={(e) => updateConfig('images', 'maxHeight', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-blue-500"
                  placeholder="px"
                />
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="border border-purple-500/30 rounded-lg p-4">
            <h5 className="text-purple-300 font-medium mb-3 flex items-center gap-2">
              <Icon name={ICONS.VIDEO} size={16} className="text-purple-400" />
              {t.workflow?.videoSettings || 'Video'}
            </h5>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxCount || 'Max Count'}
                </label>
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={config.video?.maxCount ?? ''}
                  onChange={(e) => handleMaxCountChange('video', e)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-purple-500"
                  placeholder="2"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxFileSize || 'Max Size (MB)'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.video?.maxFileSize ?? ''}
                  onChange={(e) => updateConfig('video', 'maxFileSize', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-purple-500"
                  placeholder="100"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxDuration || 'Max Duration (s)'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.video?.maxDuration ?? ''}
                  onChange={(e) => updateConfig('video', 'maxDuration', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-purple-500"
                  placeholder="60"
                />
              </div>
            </div>
          </div>

          {/* Audio Section */}
          <div className="border border-orange-500/30 rounded-lg p-4">
            <h5 className="text-orange-300 font-medium mb-3 flex items-center gap-2">
              <Icon name={ICONS.AUDIO} size={16} className="text-orange-400" />
              {t.workflow?.audioSettings || 'Audio'}
            </h5>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxCount || 'Max Count'}
                </label>
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={config.audio?.maxCount ?? ''}
                  onChange={(e) => handleMaxCountChange('audio', e)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-orange-500"
                  placeholder="3"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxFileSize || 'Max Size (MB)'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.audio?.maxFileSize ?? ''}
                  onChange={(e) => updateConfig('audio', 'maxFileSize', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-orange-500"
                  placeholder="50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {t.workflow?.maxDuration || 'Max Duration (s)'}
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.audio?.maxDuration ?? ''}
                  onChange={(e) => updateConfig('audio', 'maxDuration', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-xs focus:border-orange-500"
                  placeholder="30"
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-green-400 mt-2">
            {t.workflow?.mediaContentSettingsDesc || 'Configure limits for each media type. Leave empty for defaults.'}
          </p>
        </section>
      )}

      {/* Help Section */}
      {(hasInputImagesWidget || hasInputMediaWidget) && (
        <section className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
          <h4 className="text-sm font-medium text-blue-300 mb-2 flex items-center gap-2">
            <Icon name={ICONS.INFO} size={16} />
            {t.admin?.restrictionsTips || 'Tips'}
          </h4>
          <ul className="text-sm text-gray-400 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-0.5">•</span>
              <span>{t.admin?.restrictionsTip1 || 'Leave fields empty to use system defaults'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-0.5">•</span>
              <span>{t.admin?.restrictionsTip2 || 'Size constraints help ensure consistent output quality'}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-400 mt-0.5">•</span>
              <span>{t.admin?.restrictionsTip3 || 'File size limits prevent excessive upload times'}</span>
            </li>
          </ul>
        </section>
      )}
    </div>
  );
}
